import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HelperService } from './helper.service';
import { WorklogFilterState } from '../models/worklog.model';

/**
 * Service for managing worklog filter state
 * See docs/worklog-editor/02-architecture.md
 */
@Injectable({
  providedIn: 'root'
})
export class WorklogFilterService {
  private readonly FILTER_STATE_KEY = 'worklog_filter_state';

  private readonly defaultFilterState: WorklogFilterState = {
    recurringFilter: 'all',
    statusFilter: 'pending',  // Default to pending only
    residentFilter: null
  };

  private filterState = new BehaviorSubject<WorklogFilterState>(this.defaultFilterState);
  public filterState$: Observable<WorklogFilterState> = this.filterState.asObservable();

  constructor(private helperService: HelperService) {
    // Load saved filter state from localStorage
    const savedState = this.helperService.getStoredValue(this.FILTER_STATE_KEY, null);
    if (savedState) {
      this.filterState.next(savedState);
    }
  }

  /**
   * Get current filter state
   * @returns Current filter state
   */
  getFilterState(): WorklogFilterState {
    return this.filterState.value;
  }

  /**
   * Set recurring filter
   * @param value Recurring filter value
   */
  setRecurringFilter(value: 'all' | 'recurring' | 'non-recurring'): void {
    const newState = {
      ...this.filterState.value,
      recurringFilter: value
    };
    this.updateFilterState(newState);
  }

  /**
   * Set status filter
   * @param value Status filter value
   */
  setStatusFilter(value: 'pending' | 'all' | 'completed'): void {
    const newState = {
      ...this.filterState.value,
      statusFilter: value
    };
    this.updateFilterState(newState);
  }

  /**
   * Set resident filter
   * @param clientCode Client code or null for all
   */
  setResidentFilter(clientCode: string | null): void {
    const newState = {
      ...this.filterState.value,
      residentFilter: clientCode
    };
    this.updateFilterState(newState);
  }

  /**
   * Reset filters to default state
   */
  resetFilters(): void {
    this.updateFilterState(this.defaultFilterState);
  }

  /**
   * Update filter state and persist to localStorage
   * @param newState New filter state
   */
  private updateFilterState(newState: WorklogFilterState): void {
    this.filterState.next(newState);
    this.helperService.storeValue(this.FILTER_STATE_KEY, newState);
  }
}
